import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AppContainer } from '@mitel/cloudlink-console-components';
import { Account } from '@mitel/cloudlink-sdk';
import { ClApplauncherService } from '@mitel/cloudlink-console-components';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AccountsService } from './accounts.service';
import { AuthService } from './auth.service';

export class LauncherAppContainer extends AppContainer {
    productCode: string;
    /**
     * clientId
     * This would be the Mitel auth client code of the app that we are launching. Used to
     * generate a one-time use auth code.
     * Optional. If not supplied, no code is generated.
     * 
     */
    clientId?: string;
}

/**
 * App Launcher is that little grid on the top header bar (left of users name) that can be
 * used to launch another app to a new browser tab.
 */
@Injectable({
	providedIn: 'root'
})
export class AppLauncherService {

    private account: Account;
    private _MAX_CAPACITY: number = 9;
    /**
     * All apps that are launcher capable. This account may not have access.
     */
    private _allApps: LauncherAppContainer[] = [];
    /**
     * Apps that this account has access to
     */
    private _allowedApps$ = new BehaviorSubject<LauncherAppContainer[]>([]);
    private cloud: string = environment.cloud || '';
    public accountSet = new Subject<boolean>();

    /**
     * Set default list of apps.
     */
    private initApps() {
        this.addApp({
            label: 'Contact Center Admin',
            img64: '../../../assets/images/micc.svg',
            url: `https://cc-admin${this.cloud === 'dev' ? '.dev' : ''}.mitel.io`,
            productCode: 'MiCC', // from integrations.ts MitelIntegrations
            clientId: environment.callCenterSpa.clientId
        });
        this.addApp({
            label: 'Workflow Studio',
            img64: '../../../assets/images/workflow-studio-logo.svg',
            url: `https://workflow${this.cloud === 'dev' ? '.dev' : ''}.mitel.io`,
            productCode: 'WorkflowStudio',
            clientId: environment.workflowSpa.clientId
        });
    }
    
    constructor(private clAppLauncherService: ClApplauncherService, accountsService: AccountsService, authService: AuthService) {
        this.initApps();
        this.clAppLauncherService.getAppClick().subscribe(appId => {
            let app = this._allApps.find(app => app.id === appId);
            if (!!app) {
                const url = new URL(app.url);
                if (!!app.clientId) {
                    authService.getIFrameAuthCode(app.url, app.clientId).then(
                        code => {
                            if (url.searchParams.has('code')) {
                                url.searchParams.set('code', code)
                            } else {
                                url.searchParams.append('code', code);
                            }
                            window.open(url);
                        })
                } else {
                    window.open(url);
                }
            }
        });
        accountsService.accountTagChanged.subscribe(
            tags => {
                const products: any[] = tags?.products ?? [];
                const allowedApps = this._allApps.filter(app => products.includes(app.productCode));
                this._allowedApps$.next(allowedApps);
            }
        );
    }

    /**
     * Set the current account being used.
     */
    setAccount(account: Account) {
        this.account = account;
        if ( !!account)
            this.accountSet.next(true);
    }

    getAccount() {
        return this.account;
    }

    /**
     * Return the app launcher list. Updated when list changes
     */
    getAppsUpdated(): Observable<LauncherAppContainer[]> {
        return this._allowedApps$;
    }

    /**
     * Check that app is valid and can be added, then concat to the app launcher array.
     * @param app 
     */
    addApp(app: LauncherAppContainer) {
        if (this._allApps.length < this._MAX_CAPACITY) {
            if (app.label && app.img64 && app.url && app.label !== '' && app.img64 !== '' && app.url !== '') {
                
                if (this.cloud === 'dev') {
                    let isMitelDevUrl = this.validateMitelDevUrl(app.url);
                    if (!isMitelDevUrl) {
                        console.warn(`Not recommended to add a public app when running in dev environment.`);
                    }
                } else {
                    let isMitelDevUrl = this.validateMitelDevUrl(app.url);
                    if(isMitelDevUrl) {
                        console.warn(`Not recommended to add a dev app when running in public environment.`);
                    }
                }
                app.id = app.productCode;
                this._allApps.push(app);
                console.log('App was added to app launcher with id: ' + app.id);
            } else {
                console.error(`Could not add app because app is not in the right format.`);
            }
        } else {
            console.error('Could not add app because launcher is full. App launcher has a maximum capacity of: ', this._MAX_CAPACITY);
        } 
    }


    /**
     * Set apps to an empty array.
     */
    removeAllApps() {
        this._allApps = [];
    }

    /**
     * Use regex to check if the provided url is a mitel dev application.
     * @param url 
     */
    private validateMitelDevUrl(url: string): boolean {
		const URL_REGEX = new RegExp(/^.*\.dev.mitel.io$/);
        return URL_REGEX.test(url);
	}
}